/**
 * /admin/matches/:matchId/video-slicing/ — slicer pipeline truthfulness.
 *
 * Pairs with the Batch 2 backend additions (commit 11145cb):
 *   GET  /api/admin/v2/matches/<id>/video-slicing-status/
 *   POST /api/admin/v2/matches/<id>/video-slicing/reconcile/
 *
 * Surfaces the by-state breakdown, recent failure traces, and a sample
 * of READY rows whose S3 file actually 404s — the "~6,800 orphan rows"
 * the founder discovered. The Reconcile button triggers the management
 * command inline (capped at 1000 rows per click); for the big legacy
 * backfill use `python manage.py reconcile_video_slicing --include-ready
 * --max-rows 5000` from the box instead.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  RefreshCw, AlertTriangle, CheckCircle2, Clock, XCircle,
  ShieldCheck, ArrowLeft, Play, Wand2,
} from "lucide-react";
import api from "../../api/axios";

const STATE_META = {
  pending:    { label: "Pending",    color: "text-text-secondary", icon: Clock },
  processing: { label: "Processing", color: "text-amber-300",      icon: Wand2 },
  ready:      { label: "Ready",      color: "text-emerald-300",    icon: CheckCircle2 },
  failed:     { label: "Failed",     color: "text-rose-300",       icon: XCircle },
  verified:   { label: "Verified",   color: "text-blue-300",       icon: ShieldCheck },
};

export default function AdminVideoSlicingStatus() {
  const { matchId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileSummary, setReconcileSummary] = useState(null);
  const [includeReady, setIncludeReady] = useState(false);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const { data: payload } = await api.get(
        `/api/admin/v2/matches/${matchId}/video-slicing-status/`,
      );
      setData(payload);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }

  async function reconcile() {
    setReconciling(true);
    setReconcileSummary(null);
    try {
      const { data: payload } = await api.post(
        `/api/admin/v2/matches/${matchId}/video-slicing/reconcile/`,
        { include_ready: includeReady },
      );
      setReconcileSummary(payload.summary || "Reconcile completed.");
      await load();
    } catch (e) {
      setReconcileSummary(
        "Reconcile failed: " + (e?.response?.data?.detail || e.message),
      );
    } finally {
      setReconciling(false);
    }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [matchId]);

  return (
    <div className="p-6 space-y-4">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs text-text-secondary">
            <Link to={`/admin/matches/${matchId}/videos`} className="hover:underline flex items-center gap-1">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to videos
            </Link>
          </div>
          <h1 className="text-2xl font-semibold mt-1">Video slicing status</h1>
          <p className="text-sm text-text-secondary mt-1">
            Per-ball clip pipeline truthfulness for this match — what's ready,
            what's processing, what failed, and which READY rows are
            actually orphans (S3 file missing).
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border-default text-sm hover:bg-bg-secondary"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </button>
      </header>

      {error && (
        <div className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 p-3 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          <span>{error}</span>
        </div>
      )}

      {data?.advisory && (
        <div className="rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-200 p-3 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="min-w-0">{data.advisory}</div>
        </div>
      )}

      {data && (
        <>
          {/* State breakdown KPI strip */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {Object.entries(STATE_META).map(([key, meta]) => {
              const n = data.by_state?.[key] || 0;
              const Icon = meta.icon;
              return (
                <div key={key} className="rounded-lg border border-border-default bg-bg-primary p-3">
                  <div className={`flex items-center gap-2 text-[10px] uppercase tracking-wider ${meta.color}`}>
                    <Icon className="w-3.5 h-3.5" />
                    {meta.label}
                  </div>
                  <div className="font-mono text-2xl mt-1">{n.toLocaleString("en-IN")}</div>
                </div>
              );
            })}
          </div>

          <div className="text-xs text-text-secondary">
            {data.total_with_video_row}/{data.total_balls} balls have a BallVideo row · sampled-orphan rate{" "}
            <span className={data.sampled_orphan_rate > 0.5 ? "text-rose-300" : "text-text-primary"}>
              {(data.sampled_orphan_rate * 100).toFixed(0)}%
            </span>{" "}
            (of {data.sampled_orphans?.length === 0 ? 10 : data.sampled_orphans.length} HEAD-checked READY rows)
          </div>

          {/* Reconcile control */}
          <div className="rounded-lg border border-border-default bg-bg-primary p-4 space-y-3">
            <h3 className="text-sm font-semibold">Reconcile</h3>
            <p className="text-xs text-text-secondary">
              Walks PROCESSING (and optionally READY) BallVideo rows, S3
              HEADs the per-ball clip, and flips state to READY (file
              exists) or FAILED (still missing past the fail-after
              threshold). Capped at 1,000 rows per click — use the
              management command (`reconcile_video_slicing`) from the
              box for larger backfills.
            </p>
            <label className="flex items-center gap-2 text-xs cursor-pointer">
              <input
                type="checkbox"
                checked={includeReady}
                onChange={(e) => setIncludeReady(e.target.checked)}
                className="accent-button-primary"
              />
              <span>
                Include READY rows (legacy-orphan backfill). Use this when
                the advisory above flagged a high orphan rate.
              </span>
            </label>
            <div className="flex items-center gap-2">
              <button
                onClick={reconcile}
                disabled={reconciling}
                className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-button-primary hover:opacity-90 text-white text-sm font-semibold disabled:opacity-50"
              >
                <Wand2 className={`w-3.5 h-3.5 ${reconciling ? "animate-pulse" : ""}`} />
                {reconciling ? "Reconciling…" : "Reconcile now"}
              </button>
            </div>
            {reconcileSummary && (
              <pre className="text-[11px] font-mono whitespace-pre-wrap rounded border border-border-default bg-bg-secondary/40 p-2 text-text-secondary">
                {reconcileSummary}
              </pre>
            )}
          </div>

          {/* Recent failures */}
          <section className="rounded-lg border border-border-default bg-bg-primary overflow-hidden">
            <div className="px-4 py-3 border-b border-border-default flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <XCircle className="w-4 h-4 text-rose-300" />
                Recent failures
              </h3>
              <span className="text-xs text-text-secondary">
                {data.recent_failures?.length || 0} shown (latest 20)
              </span>
            </div>
            {(!data.recent_failures || data.recent_failures.length === 0) ? (
              <div className="p-6 text-center text-sm text-text-secondary">
                No FAILED rows for this match.
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-table-header text-left text-[10px] uppercase tracking-widest text-text-secondary">
                  <tr>
                    <th className="px-3 py-2">When</th>
                    <th className="px-3 py-2">Inn</th>
                    <th className="px-3 py-2">Ball</th>
                    <th className="px-3 py-2">Error</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_failures.map((r) => (
                    <tr key={r.ball_id} className="border-t border-border-default">
                      <td className="px-3 py-2 text-text-secondary tabular-nums">
                        {new Date(r.last_state_at).toLocaleString()}
                      </td>
                      <td className="px-3 py-2">{r.innings}</td>
                      <td className="px-3 py-2 font-mono">{r.over}.{r.ball_in_over}</td>
                      <td className="px-3 py-2 text-rose-300 max-w-[600px]">
                        <div className="truncate">{r.error_message}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>

          {/* Sampled orphans */}
          <section className="rounded-lg border border-border-default bg-bg-primary overflow-hidden">
            <div className="px-4 py-3 border-b border-border-default flex items-center justify-between">
              <h3 className="text-sm font-semibold flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-300" />
                Sampled orphans (READY rows with no S3 file)
              </h3>
              <span className="text-xs text-text-secondary">
                Random sample of ≤10
              </span>
            </div>
            {(!data.sampled_orphans || data.sampled_orphans.length === 0) ? (
              <div className="p-6 text-center text-sm text-text-secondary">
                No orphans in the random READY sample. 👍
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-table-header text-left text-[10px] uppercase tracking-widest text-text-secondary">
                  <tr>
                    <th className="px-3 py-2">Inn</th>
                    <th className="px-3 py-2">Ball</th>
                    <th className="px-3 py-2">Expected S3 key</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sampled_orphans.map((r) => (
                    <tr key={r.ball_id} className="border-t border-border-default">
                      <td className="px-3 py-2">{r.innings}</td>
                      <td className="px-3 py-2 font-mono">{r.over}.{r.ball_in_over}</td>
                      <td className="px-3 py-2 font-mono text-text-secondary max-w-[700px]">
                        <div className="truncate">{r.video_source_id}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </section>
        </>
      )}
    </div>
  );
}
